import classNames from 'classnames';
import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';

import { computePlacement, type Placement, type PlacementSide } from '../helpers/placement';
import { useIsMounted } from '../hooks/useIsMounted';

import './Tooltip.css';

/*
 * For how to use guide see Tooltip.md
 */

export type TooltipProps = {
    children?: React.ReactNode;
    className?: string;
    tooltipClassName?: string;
    tooltipText?: string | React.ReactNode;
    fadeInDuration?: TooltipFadeInDuration;
    delay?: number;
    direction?: TooltipDirection;
    usePortal?: boolean; // New prop to enable portal rendering
    isDisabled?: boolean;
};

export enum TooltipDirection {
    Top = 'top',
    Right = 'right',
    Bottom = 'bottom',
    Left = 'left',
    TopLeft = 'top-left',
    TopRight = 'top-right',
    BottomLeft = 'bottom-left',
    BottomRight = 'bottom-right'
}

/** The side of the trigger a portaled tooltip ended up on, after flipping when it did not fit. */
type TooltipPlacement = PlacementSide;

const PLACEMENT_OF: Record<TooltipDirection, Placement> = {
    [TooltipDirection.Top]: { side: 'top', align: 'center' },
    [TooltipDirection.TopLeft]: { side: 'top', align: 'start' },
    [TooltipDirection.TopRight]: { side: 'top', align: 'end' },
    [TooltipDirection.Bottom]: { side: 'bottom', align: 'center' },
    [TooltipDirection.BottomLeft]: { side: 'bottom', align: 'start' },
    [TooltipDirection.BottomRight]: { side: 'bottom', align: 'end' },
    [TooltipDirection.Left]: { side: 'left', align: 'center' },
    [TooltipDirection.Right]: { side: 'right', align: 'center' }
};

export enum TooltipFadeInDuration {
    _75 = 'duration-75',
    _100 = 'duration-100',
    _150 = 'duration-150',
    _200 = 'duration-200',
    _300 = 'duration-300',
    _500 = 'duration-500',
    _700 = 'duration-700',
    _1000 = 'duration-1000'
}

function Tooltip(props: TooltipProps): React.JSX.Element {
    const showTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
    const hiddenTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
    const [active, setActive] = useState(false);
    const [hidden, setHidden] = useState(true);
    const [position, setPosition] = useState<{
        top: number;
        left: number;
        arrowLeft: number;
        arrowTop: number;
        placement: TooltipPlacement;
    }>({ top: 0, left: 0, arrowLeft: 0, arrowTop: 0, placement: 'top' });
    // Only render portal on client to avoid hydration mismatch
    const isMounted = useIsMounted();
    const wrapperRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);

    const showTip = () => {
        // Touch-only devices: a tap fires a synthetic mouseenter but never a mouseleave, so the
        // tooltip would appear and then stick around (even on top of the modal the tap opened).
        // Hover affordances are pointless without a hover-capable pointer — skip entirely.
        if (typeof window !== 'undefined' && window.matchMedia?.('(hover: none)').matches) {
            return;
        }
        setHidden(false);
        clearTimeout(hiddenTimeoutRef.current);
        showTimeoutRef.current = setTimeout(() => {
            setActive(true);
            if (props.usePortal && wrapperRef.current) {
                const rect = wrapperRef.current.getBoundingClientRect();
                // The tooltip is already rendered (just transparent), so its real size can be measured.
                const size = {
                    width: tooltipRef.current?.offsetWidth ?? 0,
                    height: tooltipRef.current?.offsetHeight ?? 0
                };
                const { top, left, side } = computePlacement(
                    rect,
                    size,
                    PLACEMENT_OF[props.direction || TooltipDirection.Top],
                    { width: window.innerWidth, height: window.innerHeight }
                );

                // Keep the arrow anchored to the trigger's centre even when the tooltip body was
                // shifted by the viewport clamping (and a safe distance from the rounded corners).
                const arrowInset = 10;
                const anchor = (center: number, start: number, extent: number) =>
                    Math.min(Math.max(center - start, arrowInset), Math.max(arrowInset, extent - arrowInset));
                const isVertical = side === 'top' || side === 'bottom';
                setPosition({
                    top,
                    left,
                    arrowLeft: isVertical ? anchor(rect.left + rect.width / 2, left, size.width) : 0,
                    arrowTop: isVertical ? 0 : anchor(rect.top + rect.height / 2, top, size.height),
                    placement: side
                });
            }
        }, props.delay || 50);
    };

    const hideTip = () => {
        clearTimeout(showTimeoutRef.current);
        setActive(false);
        hiddenTimeoutRef.current = setTimeout(() => {
            setHidden(true);
        }, props.delay || 50);
    };

    // A portaled tip may have flipped to another side of the trigger; the direction class carries
    // the CSS arrow, so it follows the side the tip actually ended up on.
    const directionClass = props.usePortal ? position.placement : props.direction || TooltipDirection.Top;

    const tooltipElement = (
        <div
            ref={props.usePortal ? tooltipRef : undefined}
            className={classNames(
                `Tooltip-Tip transition-opacity ${active ? 'opacity-100' : 'opacity-0'} ${hidden ? 'hidden' : ''}`,
                directionClass,
                props.fadeInDuration || TooltipFadeInDuration._150,
                props.tooltipClassName
            )}
            style={
                props.usePortal
                    ? {
                          position: 'fixed',
                          top: position.top,
                          // .Tooltip-Tip.bottom / .left position inline tips via `bottom` / `right` —
                          // neutralise them, the portal is fully positioned through `top` and `left`.
                          bottom: 'auto',
                          right: 'auto',
                          left: position.left,
                          zIndex: 9999,
                          transform: 'none',
                          // Consumed by .Tooltip-Tip::before so the arrow keeps pointing at the
                          // trigger even when the body was clamped to the viewport.
                          ['--tooltip-arrow-left' as string]: `${position.arrowLeft}px`,
                          ['--tooltip-arrow-top' as string]: `${position.arrowTop}px`
                      }
                    : undefined
            }>
            {props.tooltipText}
        </div>
    );

    if (props.isDisabled) {
        return props.children as React.ReactElement;
    }

    return (
        <div
            ref={wrapperRef}
            // z-modal-view only for inline (non-portal) tips, which live inside this wrapper and need
            // it to float above neighbours. When portaled, the tip renders at document.body with its
            // own zIndex, so lifting the wrapper (and thus the trigger) to 9000 just makes the trigger
            // cover unrelated overlays like the nav profile dropdown.
            className={classNames(props.className, 'Tooltip-Wrapper', !props.usePortal && 'z-modal-view')}>
            {/* onClick hides the tip: clicking the trigger usually opens an overlay (modal/menu),
            and a lingering tooltip would sit on top of it (portal tips render at z 9999). */}
            <div
                onMouseEnter={showTip}
                onMouseLeave={hideTip}
                onClick={hideTip}>
                {props.children}
            </div>
            {props.usePortal ? (isMounted ? createPortal(tooltipElement, document.body) : null) : tooltipElement}
        </div>
    );
}

export default Tooltip;
